/**
 * @kia/shared — accounting (حسابداری دوطرفه) contracts.
 *
 * The double-entry backbone that lets the Control Tower own its books instead of Sepidar. Kept as a
 * SEPARATE, self-contained section (its own schemas, tables, domain, routes) so accounting concerns
 * never bleed into the operational sales/cash code. Every value is an INTEGER rial (no floats).
 *
 * Core invariant (enforced in the domain, not just here): for every journal entry Σdebit = Σcredit,
 * every line is EITHER a debit OR a credit (never both, never negative), and a posted entry is
 * immutable — a mistake is fixed with a reversing entry, never an edit.
 */
import { z } from 'zod';
import { normalizeDigits } from './normalize.js';
import { MAX_RIAL } from './money.js';
import { rialInput, quantityInput } from './moneySchema.js';
/** The five account natures. Normal balance: asset/expense = debit; liability/equity/revenue = credit. */
export const ACCOUNT_TYPES = ['asset', 'liability', 'equity', 'revenue', 'expense'];
export const ACCOUNT_TYPE_LABELS_FA = {
    asset: 'دارایی',
    liability: 'بدهی',
    equity: 'حقوق صاحبان سهام',
    revenue: 'درآمد',
    expense: 'هزینه',
};
/** Debit-normal natures increase with debits; the rest increase with credits. */
export const DEBIT_NORMAL = {
    asset: true,
    expense: true,
    liability: false,
    equity: false,
    revenue: false,
};
/**
 * Where a journal entry came from. `manual` and `opening` are hand-entered; the rest are produced by
 * the auto-posting rules from operational events (Phase 1b) and carry a `sourceRef` back to that event
 * so posting stays idempotent (one event books once) — the same discipline as `price_locks`.
 */
export const JOURNAL_SOURCES = [
    'manual',
    'opening',
    'reversal',
    'sepidar_import',
    'delivery',
    'sales_invoice',
    'cheque_in',
    'cheque_out',
    'po_receipt',
    'loan',
    'recurring',
    'deposit',
    'advance_appl', // customer advance applied to settle accepted-invoice AR (Dr CUSTOMER_ADVANCES / Cr AR)
    'work_order',
    'payroll',
    'depreciation',
];
// ── Chart of accounts ────────────────────────────────────────────────────────
export const GlAccountSchema = z.object({
    code: z
        .string()
        .trim()
        .regex(/^[0-9]{2,10}$/, 'کد حساب باید عددی (۲ تا ۱۰ رقم) باشد'),
    name: z.string().trim().min(1, 'نام حساب لازم است').max(120),
    type: z.enum(ACCOUNT_TYPES),
    parentCode: z
        .string()
        .trim()
        .regex(/^[0-9]{2,10}$/)
        .optional(),
    /** Only leaf ("postable") accounts may receive journal lines; group accounts roll up their children. */
    postable: z.boolean().default(true),
    active: z.boolean().default(true),
});
// ── Journal entries ──────────────────────────────────────────────────────────
/** A single posting line. Exactly one of debit/credit is > 0; both are non-negative integer rials. */
export const JournalLineSchema = z
    .object({
    accountCode: z
        .string()
        .trim()
        .regex(/^[0-9]{2,10}$/, 'کد حساب نامعتبر است'),
    // .max(MAX_RIAL): a value > 2^53 is a valid JS integer (passes .int()) but node:sqlite THROWS when it
    // reads the stored INTEGER back, bricking every read of the ledger. Cap it at the write (money.ts).
    debit: z.number().int().min(0).max(MAX_RIAL, 'مبلغ خارج از محدودهٔ مجاز است').default(0),
    credit: z.number().int().min(0).max(MAX_RIAL, 'مبلغ خارج از محدودهٔ مجاز است').default(0),
    description: z.string().trim().max(240).optional(),
})
    .refine((l) => l.debit > 0 !== l.credit > 0, {
    message: 'هر ردیف باید فقط بدهکار یا فقط بستانکار باشد (نه هر دو، نه هیچ‌کدام)',
});
/** Input to post a new journal entry (سند). The domain re-checks balance before it is written. */
export const JournalEntryInputSchema = z
    .object({
    date: z.string().trim().min(1), // ISO date the entry is effective (period is derived from it)
    memo: z.string().trim().max(500).default(''),
    source: z.enum(JOURNAL_SOURCES).default('manual'),
    /** Back-reference to the originating event, e.g. "cheque:42" — makes auto-posting idempotent. */
    sourceRef: z.string().trim().max(120).optional(),
    lines: z.array(JournalLineSchema).min(2, 'سند باید حداقل دو ردیف داشته باشد'),
})
    .refine((e) => {
    const d = e.lines.reduce((s, l) => s + l.debit, 0);
    const c = e.lines.reduce((s, l) => s + l.credit, 0);
    return d === c && d > 0;
}, { message: 'سند تراز نیست: جمع بدهکار باید با جمع بستانکار برابر و بزرگ‌تر از صفر باشد' });
// ── Subledger write-form schemas ──────────────────────────────────────────────
// The subledger write routes (work orders, employees, assets, opening) validate through THESE, so an
// Infinity / NaN / negative / fractional amount can never reach the ledger. `rial` is the shared money
// guard: a finite, non-negative INTEGER — the invariant the whole system rests on. `.finite()` is what
// rejects Infinity (which a bare `Number(x) > 0` lets through, and SQLite then stores as a NULL amount).
// `rial` / `positiveQty` are the SHARED validators (moneySchema.ts, CW-06) — the SAME ones the request
// schemas use, so the subledger and the sales/cash edges can never disagree on a valid amount vs quantity.
const rial = rialInput('مبلغ نامعتبر است', { positive: false });
const positiveQty = quantityInput('مقدار باید مثبت باشد');
// Canonicalize code identity at the write boundary: Persian/Arabic digits → ASCII, so the stored code matches
// the numeric master/BOM code on every downstream join (the yield-variance join in particular silently DROPS a
// consumption row whose code differs only by digit form). Same idiom the CSV import already uses.
const canonicalCode = (msg) => z
    .string()
    .trim()
    .min(1, msg)
    .max(40)
    .transform((s) => normalizeDigits(s));
export const WorkOrderInputSchema = z.object({
    productCode: canonicalCode('کد محصول لازم است'),
    qty: positiveQty,
    laborCost: rial.default(0),
    note: z.string().trim().max(240).default(''),
    materials: z
        .array(z.object({
        materialCode: canonicalCode('کد ماده لازم است'),
        qty: positiveQty,
    }))
        .min(1, 'حداقل یک ماده لازم است'),
});
export const EmployeeInputSchema = z.object({
    code: z.string().trim().max(40).default(''),
    name: z.string().trim().min(1, 'نام لازم است').max(120),
    nationalId: z.string().trim().max(20).default(''),
    baseSalary: rial,
});
export const FixedAssetInputSchema = z
    .object({
    code: z.string().trim().max(40).default(''),
    name: z.string().trim().min(1, 'نام لازم است').max(120),
    acquiredOn: z.string().trim().max(30).default(''),
    cost: rial,
    salvage: rial.default(0),
    lifeMonths: z.number().int().positive('عمر مفید باید مثبت باشد').finite(),
})
    // Salvage can never exceed cost — otherwise the depreciable base (cost − salvage) is negative.
    .refine((a) => a.salvage <= a.cost, {
    message: 'ارزش اسقاط نمی‌تواند از بهای تمام‌شده بیشتر باشد',
    path: ['salvage'],
});
export const OpeningBalancesSchema = z.object({
    date: z.string().trim().min(1, 'تاریخ لازم است'),
    rows: z
        .array(z.object({
        account: z.string().trim().min(1, 'کد حساب لازم است'),
        debit: rial.default(0),
        credit: rial.default(0),
    }))
        .min(1, 'حداقل یک مانده لازم است'),
});
//# sourceMappingURL=accounting.js.map